import { createSlice } from "@reduxjs/toolkit";
import {
  login,
  register,
  logout,
  getUserInfo,
  otpActivate,
  resendOtp,
  getChatByUserId,
  getUserChatData,
  updateUser,
  guestSession,
} from "../Thunks/AuthThunk";
import StompChatService from "../../Services/StompChatService";
import { toast } from "react-toastify";
import { removeCookie } from "../../Utils/cookiesHelper";
import {
  clearGuestSession,
  forgetGuestHandover,
  rememberGuestHandover,
} from "../../Utils/guestSession";

// Whoever signs in for real, or out, is no longer a guest, and the saved guest
// session must not come back on the next page load.
const dropGuest = (state) => {
  state.isGuest = false;
  clearGuestSession();
};

// Signing out, by choice or by force: nothing of the guest survives, including
// anything kept for a handover that is now never going to happen. The next
// person to use this browser must not inherit it.
const dropGuestAndHandover = (state) => {
  dropGuest(state);
  forgetGuestHandover();
};

// Keep what the handover needs before the guest session is thrown away.
//
// A visitor who signs up inside the chat has just spent a conversation telling
// the shop what they want, and all of it is filed against the guest they were a
// second ago. Moving it onto the new account needs two things the backend will
// not take on trust: which conversation it was, and the guest's own token as
// proof that the caller is that visitor. Both live in the guest session, and
// dropGuest above used to destroy them — so the call that moves the history
// could never be made, and in four months it never was.
//
// Stamped with the account it was captured for, and kept out of the store on
// purpose: see rememberGuestHandover. A reducer may not speak to the network,
// so handOverGuestConversation makes the call.
const rememberGuestForHandover = (state, action) => {
  if (!state.isGuest) return;
  const guestSessionId = state.user?.guestSessionId;
  const guestToken = state.token;
  const forUserId = action?.payload?.userInfo?.id;
  if (!guestSessionId || !guestToken || !forUserId) return;
  rememberGuestHandover({
    guestSessionId,
    guestToken,
    forUserId,
    orgId: state.user?.organizationId ?? null,
  });
};

const activateOtoSuccessMessage =
  "Your account has been activated successfully.";
const registerSuccessMessage = "Check your email for the activation code.";

export const AuthSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    userChatData: null, // Store user chat data (userId, name, email, etc.)
    token: null,
    chatId: null,
    isLoading: false,
    isLoggedIn: false,
    // Signed in only as far as the conversation goes: a visitor with a guest
    // token. Profile, basket and history stay behind a real sign-in.
    isGuest: false,
    error: null,
    visitor: null,
    room: null,
    message: null,
    savedAt: null,
  },
  reducers: {
    setIsLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    forceLogout: (state) => {
      state.user = null;
      state.userChatData = null;
      state.token = null;
      state.chatId = null;
      state.isLoggedIn = false;
      state.savedAt = null;
      dropGuestAndHandover(state);
      removeCookie("jwtToken");

      // Disconnect WebSocket on force logout
      StompChatService.disconnect();
    },
    clearError: (state, action) => {
      state.error = null;
    },
    forceLogoutWithClearMessages: (state) => {
      state.user = null;
      state.userChatData = null;
      state.token = null;
      state.chatId = null;
      state.isLoggedIn = false;
      state.savedAt = null;
      dropGuestAndHandover(state);
      removeCookie("jwtToken");

      StompChatService.disconnect();
    },
    setChatId: (state, action) => {
      state.chatId = action.payload;
    },
    // A guest session saved earlier in this tab. The socket connects through
    // the usual step once isLoggedIn and token are set.
    restoreGuestSession: (state, action) => {
      const { token, user, chatId } = action.payload || {};
      if (!token || !user?.id) return;
      state.token = token;
      state.user = user;
      // Which conversation is theirs. Without it a returning guest is shown the
      // greeting, because nothing knows there is anything to go back to.
      if (chatId) state.chatId = chatId;
      state.isLoggedIn = true;
      state.isGuest = true;
      state.savedAt = Date.now();
    },
    updateUserImage: (state, action) => {
      if (state.user) {
        state.user.imageUrl = action.payload;
        state.user.image_url = action.payload; // Keep legacy field for compatibility
      }
    },
    initializeWebSocketConnection: (state) => {
      // Initialize WebSocket connection if token exists but connection is not established
      if (state.token && state.isLoggedIn) {
        const connectionStatus = StompChatService.getConnectionStatus();
        if (!connectionStatus.connected) {
          StompChatService.connect(
            state.token,
            null,
            null, // Message handlers registered via StompChatService.onMessage()
            null
          );
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.error = null;
      // Before the guest's token is overwritten and their session cleared.
      rememberGuestForHandover(state, action);
      state.token = action.payload.refresh;
      state.user = action.payload.userInfo;
      state.isLoggedIn = true;
      state.isLoading = false;
      state.savedAt = Date.now();
      dropGuest(state);

      // Connect to WebSocket after successful login
      if (action.payload.refresh) {
        StompChatService.connect(
          action.payload.refresh,
          null,
          null, // Message handlers registered via StompChatService.onMessage()
          null
        );
      }
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(register.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(register.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      toast.success(registerSuccessMessage);
    });
    builder.addCase(register.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(otpActivate.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(otpActivate.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      toast.success(activateOtoSuccessMessage);
      // Before the guest's token is overwritten and their session cleared. This
      // is the door the invitation leads to: a stranger who was offered an
      // account in the chat arrives here.
      rememberGuestForHandover(state, action);
      state.token = action.payload.refresh;
      state.user = action.payload.userInfo;
      state.isLoggedIn = true;
      state.savedAt = Date.now();
      dropGuest(state);

      // Connect to WebSocket after successful OTP activation
      if (action.payload.refresh) {
        StompChatService.connect(
          action.payload.refresh,
          null,
          null, // Message handlers registered via StompChatService.onMessage()
          null
        );
      }
    });
    builder.addCase(otpActivate.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload?.message;
    });

    builder.addCase(resendOtp.pending, (state, action) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(resendOtp.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      toast.success(registerSuccessMessage);
    });
    builder.addCase(resendOtp.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(logout.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state, action) => {
      state.isLoading = false;
      state.user = null;
      state.token = null;
      state.chatId = null;
      state.isLoggedIn = false;
      state.savedAt = null;
      dropGuestAndHandover(state);
      removeCookie("jwtToken");
      StompChatService.disconnect();
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(getUserInfo.pending, (state, action) => {});
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      state.user = action.payload;
    });
    builder.addCase(getUserInfo.rejected, (state, action) => {});

    // isLoading is left alone on purpose: it drives the sign-in form, and a
    // guest session is created behind a composer, not a form.
    builder.addCase(guestSession.pending, (state) => {
      state.error = null;
    });
    builder.addCase(guestSession.fulfilled, (state, action) => {
      state.error = null;
      state.token = action.payload.token;
      state.user = action.payload.user;
      if (action.payload.chatId) state.chatId = action.payload.chatId;
      state.isLoggedIn = true;
      state.isGuest = true;
      state.savedAt = Date.now();

      StompChatService.connect(
        action.payload.token,
        null,
        null, // Message handlers registered via StompChatService.onMessage()
        null
      );
    });
    builder.addCase(guestSession.rejected, (state, action) => {
      state.error = action.payload;
    });

    builder.addCase(getChatByUserId.fulfilled, (state, action) => {
      state.chatId = action.payload.chatId;
    });

    builder.addCase(getUserChatData.fulfilled, (state, action) => {
      state.userChatData = action.payload;
    });

    builder.addCase(updateUser.fulfilled, (state, action) => {
      if (action.payload?.name) {
        if (state.user) state.user.name = action.payload.name;
        if (state.userChatData) state.userChatData.name = action.payload.name;
      }
    });
  },
});

export const {
  clearError,
  setChatId,
  forceLogoutWithClearMessages,
  initializeWebSocketConnection,
  updateUserImage,
  restoreGuestSession,
} = AuthSlice.actions;
